import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LegalLayout from './LegalLayout';
import { usePrerenderSignal } from '../../utils/usePrerenderSignal';
import './LegalPage.css';

const TermsPage: React.FC = () => {
  usePrerenderSignal();
  return (
  <>
    <Helmet>
      <title>Terms of Service - PRTCL</title>
      <meta name="description" content="PRTCL's Terms of Service. PRTCL is an educational and organizational tracking tool, not medical advice. Operated by Align to Grind LLC." />
      <link rel="canonical" href="https://www.prtclapp.com/terms" />
      <meta property="og:title" content="Terms of Service - PRTCL" />
      <meta property="og:url" content="https://www.prtclapp.com/terms" />
      <meta name="robots" content="noindex,follow" />
    </Helmet>
    <LegalLayout title="Terms of Service" eyebrow="Legal" updated="Last updated: July 14, 2026">
    <div className="legal-content">
      <h2>1. Acceptance of Terms</h2>
      <p>PRTCL is owned and operated by Align to Grind LLC ("PRTCL," "we," "us," or "our"), based in Nevada, United States.</p>
      <p>By creating an account, accessing, or using PRTCL (the "App"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms").</p>
      <p>If you do not agree to these Terms, you must stop using the App and delete your account.</p>
      <p>We may update these Terms from time to time. Your continued use of the App after any changes means you accept the updated Terms. For material changes, we will provide notice through the App or via email.</p>

      <h2>2. Not Medical Advice</h2>
      <p>PRTCL is an informational, educational, and organizational tracking tool only.</p>
      <p><strong>The App does not provide:</strong></p>
      <ul>
        <li>Medical advice</li>
        <li>Pharmaceutical guidance</li>
        <li>Clinical recommendations</li>
        <li>Dosing instructions or prescriptions</li>
        <li>Treatment plans</li>
        <li>Nutritional or dietary advice</li>
      </ul>
      <p><strong>You acknowledge and agree that:</strong></p>
      <ul>
        <li>PRTCL does not diagnose, treat, cure, or prevent any disease or medical condition</li>
        <li>PRTCL does not recommend specific substances, dosages, or protocols</li>
        <li>PRTCL does not sell, distribute, or promote the purchase of any peptides, medications, or supplements</li>
        <li>All logs, reminders, calculators, trackers, educational content, and peptide library information are for informational and organizational purposes only</li>
        <li>Educational content is presented at varying complexity levels (beginner, intermediate, advanced) for accessibility, but this does not constitute personalized medical guidance</li>
        <li>You should consult a licensed healthcare professional before making any health-related decisions</li>
        <li>Use of the App is entirely at your own risk</li>
      </ul>

      <h2>3. Peptide Library and Educational Content</h2>
      <p>The App includes an educational peptide library containing information about peptides, protocols, and related topics.</p>
      <p><strong>You acknowledge and agree that:</strong></p>
      <ul>
        <li>All library content is for educational and informational purposes only</li>
        <li>Content is general and not personalized to your health situation</li>
        <li>Content may be incomplete, outdated, or not applicable to your circumstances</li>
        <li>Content is derived from publicly available research, published studies, and expert sources, but is not a substitute for professional medical advice</li>
        <li>Protocol and dosing information presented is for educational reference only and does not constitute a recommendation to use any substance</li>
        <li>You are responsible for independently verifying any information before acting on it</li>
        <li>PRTCL does not endorse, recommend, or encourage the use of any specific peptide or substance</li>
      </ul>

      <h2>4. Dose Calculator</h2>
      <p>PRTCL includes an optional Dose Calculator feature that provides reconstitution volume and syringe measurement calculations.</p>
      <p>The Dose Calculator is for educational and reference purposes only.</p>
      <p><strong>You acknowledge and agree that:</strong></p>
      <ul>
        <li>The Dose Calculator does not provide medical advice, dosing instructions, or treatment recommendations</li>
        <li>Calculations are based solely on user-entered values and are not verified by PRTCL</li>
        <li>You are solely responsible for verifying the accuracy of all inputs and outputs</li>
        <li>Errors in user-entered values may produce incorrect results</li>
        <li>You will not rely on the Dose Calculator for medical or treatment decisions</li>
        <li>You will consult a qualified healthcare professional before making any decisions regarding dosing or administration</li>
        <li>Use of the Dose Calculator is at your own risk</li>
        <li>PRTCL is not liable for any outcomes resulting from the use of the Dose Calculator</li>
      </ul>

      <h2>5. Tracking Features</h2>
      <p>PRTCL includes optional tracking features, including but not limited to:</p>
      <ul>
        <li>Injection Site Tracking</li>
        <li>Dose Logging</li>
        <li>Protocol and Cycle Tracking</li>
      </ul>
      <p><strong>You acknowledge and agree that:</strong></p>
      <ul>
        <li>Tracking features do not provide medical advice, diagnosis, or treatment recommendations</li>
        <li>All data entered is user-provided and not verified or validated by PRTCL</li>
        <li>Injection site tracking does not constitute medical guidance on injection administration</li>
        <li>Correlations, patterns, or insights displayed are informational only and should not be relied upon for medical decisions</li>
        <li>You are solely responsible for the accuracy of all data you enter</li>
        <li>You will consult a qualified healthcare professional before making any health-related decisions based on tracked data</li>
        <li>Use of tracking features is at your own risk</li>
      </ul>

      <h2>6. AI-Assisted Features</h2>
      <p>Some features in PRTCL use third-party artificial intelligence services to process information you provide:</p>
      <ul>
        <li><strong>Semantic search:</strong> When you search the peptide library or FAQ, your search query is sent to Google's Generative Language API (Gemini) to generate a numerical embedding used to find matching content. The query text is processed by Google in accordance with Google's API terms.</li>
        <li><strong>Video and protocol summaries:</strong> When you request an AI-generated summary of a video or protocol, the relevant text is sent to Anthropic's Claude API for processing.</li>
        <li><strong>Video transcripts:</strong> Video transcript fetching is performed by Supadata.ai when requested.</li>
      </ul>
      <p>PRTCL does not include your account email, identity, or other personal identifiers when sending requests to these services.</p>
      <p><strong>You acknowledge and agree that:</strong></p>
      <ul>
        <li>AI output may be incomplete, outdated, or inaccurate</li>
        <li>AI output is for informational and educational purposes only and does not constitute medical advice</li>
        <li>You will independently verify any information before acting on it</li>
        <li>You will consult a qualified healthcare professional before making any health-related decisions, regardless of any AI-generated content displayed in the App</li>
        <li>PRTCL is not liable for any decisions made based on AI-generated output</li>
      </ul>

      <h2>7. User Responsibility</h2>
      <p>You are solely responsible for:</p>
      <ul>
        <li>Any substances you choose to use, possess, or administer</li>
        <li>All data you enter into the App, including calculator inputs, tracking data, and log entries</li>
        <li>How you interpret or act on any information displayed, including calculator outputs, library content, and educational materials</li>
        <li>Compliance with all applicable laws and regulations in your jurisdiction</li>
        <li>Ensuring any substances you track comply with the laws of your jurisdiction</li>
      </ul>
      <p>PRTCL does not verify or validate user-entered data. You are responsible for keeping accurate records.</p>

      <h2>8. Intellectual Property</h2>
      <p>All content, features, and functionality of the App, including but not limited to text, graphics, logos, icons, images, audio, software, and the PRTCL name and brand, are owned by Align to Grind LLC and are protected by United States and international copyright, trademark, and other intellectual property laws.</p>
      <p>You may not:</p>
      <ul>
        <li>Copy, modify, or create derivative works based on the App</li>
        <li>Reverse engineer, decompile, or disassemble the App</li>
        <li>Remove or alter any proprietary notices or labels</li>
        <li>Use the PRTCL name, logo, or branding without our prior written consent</li>
        <li>Reproduce, distribute, or publicly display any content from the App without authorization</li>
      </ul>

      <h2>9. Subscriptions, Payments, and Refunds</h2>
      <p>PRTCL offers paid subscriptions that unlock additional features. Subscription tiers, pricing, and trial terms are displayed in the App at the point of purchase.</p>

      <h3>How You Are Billed</h3>
      <ul>
        <li><strong>iOS (App Store):</strong> Subscriptions purchased on iOS are billed through your Apple ID, processed by Apple, and managed through Apple's In-App Purchase system. PRTCL uses RevenueCat to manage subscription entitlements; PRTCL does not receive your payment card information.</li>
        <li><strong>Web:</strong> Subscriptions purchased on the web are processed by Stripe. PRTCL does not store your full payment card information; Stripe handles all card data in accordance with PCI-DSS standards.</li>
      </ul>

      <h3>Free Trials</h3>
      <p>If a free trial is offered, the full subscription price will be charged automatically at the end of the trial period unless you cancel before the trial ends. Any unused portion of a free trial is forfeited when you start a paid subscription.</p>

      <h3>Auto-Renewal</h3>
      <p>Paid subscriptions renew automatically at the end of each billing period at the then-current price unless cancelled. The exact renewal date and price are displayed in your Apple ID settings (iOS) or your PRTCL account (web).</p>

      <h3>Cancellation</h3>
      <ul>
        <li><strong>iOS:</strong> Manage or cancel your subscription at any time through your Apple ID settings. To avoid being charged for the next billing period, cancel at least 24 hours before the renewal date.</li>
        <li><strong>Web:</strong> Manage or cancel your subscription through your PRTCL account settings or by contacting <a href="mailto:support@prtclapp.com">support@prtclapp.com</a>.</li>
      </ul>
      <p>Cancellation stops future billing. You retain access to paid features through the end of the current billing period.</p>

      <h3>Refunds</h3>
      <ul>
        <li><strong>iOS:</strong> All refund requests for App Store purchases are handled by Apple under Apple's refund policy. You may request a refund at <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">reportaproblem.apple.com</a>.</li>
        <li><strong>Web:</strong> Refunds for web subscriptions are issued at PRTCL's discretion. Contact <a href="mailto:support@prtclapp.com">support@prtclapp.com</a> within 14 days of the charge to request a refund.</li>
      </ul>

      <h3>Restoring Purchases</h3>
      <p>If you reinstall the App or switch devices, you can restore an active subscription using the "Restore Purchases" option on the iOS purchase screen, or by signing into your PRTCL account on the web.</p>

      <h3>Price Changes</h3>
      <p>If we change subscription pricing, we will notify you in advance. You will have the opportunity to cancel before the new price takes effect. Continued use after the new price takes effect constitutes acceptance of the new price.</p>

      <h3>Beta Access</h3>
      <p>Some features may be offered as beta features and identified as such in the App. Beta features are provided "as-is" with no guarantee of stability, uptime, or continued availability. By using a beta feature, you understand it may change or be removed at any time.</p>

      <h2>10. Accounts and Authentication</h2>
      <p>You can create and access your account using email and password, Sign in with Apple, or Sign in with Google. When you use Sign in with Apple or Sign in with Google, that provider verifies your identity and may share your name and email address with us to set up your account.</p>
      <p><strong>You are responsible for:</strong></p>
      <ul>
        <li>Maintaining the confidentiality of your password</li>
        <li>Maintaining the security of your account</li>
        <li>All activity that occurs under your account</li>
        <li>Notifying us immediately of any unauthorized use of your account</li>
      </ul>
      <p>You may not share your login credentials with others. PRTCL is not responsible for any unauthorized access resulting from your failure to safeguard your account credentials.</p>

      <h2>11. Data and Privacy</h2>
      <p>Your use of the App is also governed by our <Link to="/privacy">Privacy Policy</Link>.</p>
      <p>If you are a Washington State resident, your use of the App is also governed by our <Link to="/consumer-health-data">Consumer Health Data Privacy Policy</Link>.</p>
      <p>By using PRTCL, you consent to the collection and use of your information as described in the Privacy Policy.</p>
      <p>Tracking data, including injection sites, dose logs, and other logged information, is stored in your account and accessible only to you.</p>

      <h2>12. Age Requirement</h2>
      <p>You must be at least 18 years old to use the App.</p>
      <p>If we learn a user is under 18, the account may be terminated and associated data deleted.</p>

      <h2>13. Prohibited Conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the App for any unlawful purpose</li>
        <li>Attempt to gain unauthorized access to the App, other user accounts, or our systems</li>
        <li>Interfere with or disrupt the App’s functionality</li>
        <li>Upload malicious code, viruses, or harmful data</li>
        <li>Use the App to harass, abuse, or harm others</li>
        <li>Misrepresent your identity or affiliation</li>
        <li>Use automated systems (bots, scrapers) to access the App without our written permission</li>
        <li>Resell, redistribute, or sublicense access to the App</li>
      </ul>

      <h2>14. Notifications</h2>
      <p>PRTCL may send reminders and push notifications, including scheduled dose reminders and other tracking reminders you configure.</p>
      <p><strong>You acknowledge that:</strong></p>
      <ul>
        <li>Delivery of notifications is not guaranteed</li>
        <li>Notifications may be delayed or missed due to device settings, network conditions, or system issues</li>
        <li>You remain responsible for managing your own schedule and health decisions</li>
        <li>PRTCL is not liable for missed or delayed notifications</li>
      </ul>

      <h2>15. Limitation of Liability</h2>
      <p>To the fullest extent permitted by law, PRTCL and Align to Grind LLC shall not be liable for:</p>
      <ul>
        <li>Any health outcomes, injuries, or adverse effects</li>
        <li>Incorrect or incomplete user data</li>
        <li>Incorrect or incomplete calculator outputs resulting from user-entered values</li>
        <li>Reliance on tracked data, calculator results, library content, or other informational content</li>
        <li>Missed or delayed notifications</li>
        <li>Loss of data or service interruptions</li>
        <li>Decisions made based on information in the App</li>
        <li>Any indirect, incidental, special, or consequential damages</li>
        <li>Any harm resulting from substances you choose to use, possess, or administer</li>
      </ul>
      <p><strong>The App is provided "as is" and "as available" without warranties of any kind, either express or implied</strong>, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</p>
      <p>In no event shall the total liability of PRTCL or Align to Grind LLC exceed the amount you paid, if any, to use the App in the twelve (12) months preceding the claim.</p>

      <h2>16. Indemnification</h2>
      <p>You agree to indemnify, defend, and hold harmless PRTCL and Align to Grind LLC, and their officers, directors, employees, agents, and affiliates, from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:</p>
      <ul>
        <li>Your use of the App</li>
        <li>Your violation of these Terms</li>
        <li>Your violation of applicable laws or regulations</li>
        <li>Any health outcomes related to substances you track, use, possess, or administer</li>
        <li>Any content or data you submit to the App</li>
        <li>Your infringement of any third-party rights</li>
      </ul>

      <h2>17. Apple Licensed Application End User License Agreement</h2>
      <p>You acknowledge and agree that:</p>
      <ul>
        <li>These Terms are between you and PRTCL only, not with Apple Inc.</li>
        <li>PRTCL, not Apple, is solely responsible for the App and its content</li>
        <li>The App is licensed to you on a limited, non-transferable, non-exclusive basis for use on Apple-branded products that you own or control, as permitted by the Usage Rules set forth in the Apple Media Services Terms of Service</li>
        <li>PRTCL is responsible for providing any maintenance and support services for the App. Apple has no obligation to provide maintenance and support services</li>
        <li>In the event of any failure of the App to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any) to you. To the maximum extent permitted by applicable law, Apple has no other warranty obligation with respect to the App</li>
        <li>PRTCL, not Apple, is responsible for addressing any claims relating to the App, including but not limited to product liability claims, any claim that the App fails to conform to any applicable legal or regulatory requirement, and claims arising under consumer protection, privacy, or similar legislation</li>
        <li>In the event of any third-party claim that the App or your possession and use of the App infringes a third party’s intellectual property rights, PRTCL, not Apple, is solely responsible for the investigation, defense, settlement, and discharge of any such claim</li>
        <li>Apple and its subsidiaries are third-party beneficiaries of these Terms, and upon your acceptance, Apple will have the right to enforce these Terms against you as a third-party beneficiary</li>
      </ul>
      <p>You represent and warrant that (i) you are not located in a country that is subject to a U.S. Government embargo, or that has been designated by the U.S. Government as a "terrorist supporting" country; and (ii) you are not listed on any U.S. Government list of prohibited or restricted parties.</p>

      <h2>18. Dispute Resolution</h2>
      <h3>Arbitration Agreement</h3>
      <p>Any dispute, claim, or controversy arising out of or relating to these Terms or the use of the App shall be resolved through binding arbitration conducted in accordance with the rules of the American Arbitration Association ("AAA"). The arbitration shall take place in Clark County, Nevada.</p>
      <p>You agree that arbitration shall be conducted on an individual basis and not in a class, collective, or representative action.</p>
      <h3>Class Action Waiver</h3>
      <p>By agreeing to these Terms, you agree that any claims or disputes with PRTCL must be resolved on an individual basis and not as a plaintiff or class member in any purported class, collective, or representative action. You expressly waive your right to file a class action or seek relief on a class-wide basis.</p>
      <h3>Small Claims Exception</h3>
      <p>Notwithstanding the above, either party may bring an individual action in small claims court in Clark County, Nevada, if the claim falls within the court’s jurisdictional limits.</p>

      <h2>19. Changes to the App</h2>
      <p>We may modify, update, suspend, or discontinue any part of the App at any time without prior notice. We are not liable to you or any third party for any modification, suspension, or discontinuation of the App.</p>

      <h2>20. Termination</h2>
      <p>We may suspend or terminate your account if you violate these Terms or misuse the App.</p>
      <p>You may stop using the App and delete your account at any time. To request account deletion, contact support@prtclapp.com.</p>
      <p>Upon termination:</p>
      <ul>
        <li>Your right to use the App ceases immediately</li>
        <li>We may delete your account data in accordance with our Privacy Policy</li>
        <li>Provisions that by their nature should survive termination (including Limitation of Liability, Indemnification, Dispute Resolution, and Governing Law) will survive</li>
      </ul>

      <h2>21. Severability</h2>
      <p>If any provision of these Terms is found to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect.</p>

      <h2>22. Entire Agreement</h2>
      <p>These Terms, together with our <Link to="/privacy">Privacy Policy</Link>, constitute the entire agreement between you and PRTCL regarding the use of the App and supersede any prior agreements.</p>

      <h2>23. Governing Law</h2>
      <p>These Terms shall be governed by and interpreted in accordance with the laws of the State of Nevada, without regard to conflict-of-law principles.</p>
      <p>Any legal proceedings not subject to arbitration shall be brought exclusively in the state or federal courts located in Clark County, Nevada.</p>

      <div className="legal-contact">
        <h2>24. Contact</h2>
        <p>If you have questions about these Terms:</p>
        <p>Align to Grind LLC<br />Email: <a href="mailto:support@prtclapp.com">support@prtclapp.com</a></p>
      </div>
    </div>
    </LegalLayout>
  </>
  );
};

export default TermsPage;
